import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Typography, Alert, Paper, Tabs, Tab, Chip,
  CircularProgress, TextField, MenuItem, Stack, DialogActions,
  Grid, FormControlLabel, Checkbox,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import {
  getCampaigns, createCampaign, deleteCampaign,
  getInteractionTemplates, getIcpProfiles, getCampaignAnalytics, getLeadSegmentations,
} from '../services/api';
import Modal from '../components/Modal';
import { useDialog } from '../components/DialogProvider';
import AzotteGrid from '../components/AzotteGrid';

const STATUS_COLORS = { Draft: '#3b82f6', Active: '#22c55e', Paused: '#9ca3af', Completed: '#8b5cf6', Archived: '#374151' };
const TYPE_CHIP_COLORS = { Email: 'primary', LinkedIn: 'info', MultiChannel: 'warning' };
const TONES = ['Formal', 'Direct', 'Friendly', 'Consultative'];
const PERSONAS = ['CTO', 'CFO', 'ProductManager', 'CEO', 'VP_Engineering', 'VP_Sales', 'Other'];

export default function EngagePage() {
  const navigate = useNavigate();
  const dialog = useDialog();
  const [activeTab, setActiveTab] = useState(0);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [createModal, setCreateModal] = useState(false);
  const [form, setForm] = useState({});
  const [templates, setTemplates] = useState([]);
  const [icpProfiles, setIcpProfiles] = useState([]);
  const [segmentations, setSegmentations] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [analyticsData, setAnalyticsData] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [analyticsPage, setAnalyticsPage] = useState(0);
  const [analyticsRowsPerPage, setAnalyticsRowsPerPage] = useState(10);

  const fetchCampaigns = useCallback(async () => {
    try {
      const { data } = await getCampaigns();
      setCampaigns(data);
    } catch { setError('Failed to load campaigns'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchCampaigns(); }, [fetchCampaigns]);

  useEffect(() => {
    if (activeTab === 1) {
      Promise.all(campaigns.map(c => getCampaignAnalytics(c.id).then(r => r.data).catch(() => null)))
        .then(results => {
          const valid = results.filter(Boolean);
          setAnalyticsData({
            totalSent: valid.reduce((s, a) => s + a.sent, 0),
            totalOpened: valid.reduce((s, a) => s + a.opened, 0),
            totalReplied: valid.reduce((s, a) => s + a.replied, 0),
            totalBounced: valid.reduce((s, a) => s + a.bounced, 0),
            campaigns: campaigns.map((c, i) => ({ ...c, analytics: valid[i] })),
          });
        });
    }
  }, [activeTab, campaigns]);

  const openCreateModal = async () => {
    setForm({ name: '', description: '', type: 'Email', interactionTemplateId: '', targetSegmentationId: '', targetIcpProfileId: '', useAiPersonalization: false, aiProviderName: '', defaultTone: 'Direct', defaultPersona: '', startDate: '', endDate: '' });
    try {
      const [t, p, segs] = await Promise.all([
        getInteractionTemplates({ channel: 'Email' }),
        getIcpProfiles(),
        getLeadSegmentations(),
      ]);
      setTemplates((t.data || []).filter(x => x.isActive !== false));
      setIcpProfiles(p.data);
      setSegmentations(Array.isArray(segs.data) ? segs.data : (segs.data?.items || []));
    } catch { }
    setCreateModal(true);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const payload = {
        ...form,
        interactionTemplateId: form.interactionTemplateId ? parseInt(form.interactionTemplateId) : null,
        targetIcpProfileId: form.targetIcpProfileId ? parseInt(form.targetIcpProfileId) : null,
        targetSegmentationId: form.targetSegmentationId ? parseInt(form.targetSegmentationId) : null,
        defaultPersona: form.defaultPersona || null,
        startDate: form.startDate || null,
        endDate: form.endDate || null,
      };
      const { data } = await createCampaign(payload);
      setCreateModal(false);
      navigate(`/engage/campaigns/${data.id}`);
    } catch (err) { setError(err.response?.data?.message || 'Failed to create campaign'); }
  };

  const handleDelete = async (id) => {
    if (!(await dialog.confirm({ title: 'Delete campaign', message: 'Delete this campaign?', confirmText: 'Delete', danger: true }))) return;
    try {
      await deleteCampaign(id);
      fetchCampaigns();
    } catch (err) { setError(err.response?.data?.message || 'Failed to delete'); }
  };

  const filtered = campaigns.filter(c =>
    (!statusFilter || c.status === statusFilter) &&
    (!typeFilter || c.type === typeFilter)
  );

  const totalRecipients = campaigns.reduce((s, c) => s + c.recipientCount, 0);
  const activeCampaigns = campaigns.filter(c => c.status === 'Active').length;
  const avgOpenRate = campaigns.length > 0
    ? (campaigns.reduce((s, c) => s + c.openRate, 0) / campaigns.length).toFixed(1)
    : '0.0';

  const campaignColumns = [
    { key: 'name', header: 'Name', render: (val) => <Typography sx={{ fontWeight: 600 }}>{val}</Typography> },
    { key: 'type', header: 'Type', render: (val) => <Chip label={val} color={TYPE_CHIP_COLORS[val] || 'default'} size="small" />, hideOnMobile: true },
    { key: 'status', header: 'Status', render: (val) => <Typography sx={{ fontWeight: 600, color: STATUS_COLORS[val] || '#718096' }}>{val}</Typography> },
    { key: 'recipientCount', header: 'Recipients', hideOnMobile: true },
    { key: 'sentCount', header: 'Sent', hideOnMobile: true, hideOnTablet: true },
    { key: 'openRate', header: 'Open %', render: (val) => `${val}%`, hideOnMobile: true, hideOnTablet: true },
    { key: 'replyRate', header: 'Reply %', render: (val) => `${val}%`, hideOnMobile: true, hideOnTablet: true },
    { key: 'startDate', header: 'Start Date', render: (val) => val ? new Date(val).toLocaleDateString() : '-', hideOnMobile: true },
    { key: 'actions', header: '', width: 80, render: (_, row) => (
      <Button size="small" color="error" onClick={(e) => { e.stopPropagation(); handleDelete(row.id); }}>Del</Button>
    )},
  ];

  const analyticsComparisonColumns = [
    { key: 'name', header: 'Campaign', render: (val) => <Typography sx={{ fontWeight: 500 }}>{val}</Typography> },
    { key: 'analyticsSent', header: 'Sent', render: (_, row) => row.analytics?.sent },
    { key: 'analyticsOpenRate', header: 'Open %', render: (_, row) => (
      <Typography sx={{ color: row.analytics?.openRate >= 20 ? '#22c55e' : '#ef4444' }}>{row.analytics?.openRate}%</Typography>
    )},
    { key: 'analyticsReplyRate', header: 'Reply %', render: (_, row) => (
      <Typography sx={{ color: row.analytics?.replyRate >= 5 ? '#22c55e' : '#ef4444' }}>{row.analytics?.replyRate}%</Typography>
    )},
    { key: 'analyticsBounceRate', header: 'Bounce %', render: (_, row) => (
      <Typography sx={{ color: row.analytics?.bounceRate > 5 ? '#ef4444' : '#22c55e' }}>{row.analytics?.bounceRate}%</Typography>
    )},
  ];

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Engage</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateModal}>Create Campaign</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 3 }}>
        <Tab label="Campaigns" />
        <Tab label="Analytics" />
      </Tabs>

      {activeTab === 0 && (
        <Box>
          {/* Stats */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              { label: 'Total Campaigns', value: campaigns.length, color: 'primary.main' },
              { label: 'Active', value: activeCampaigns, color: '#22c55e' },
              { label: 'Total Recipients', value: totalRecipients, color: 'primary.main' },
              { label: 'Avg Open Rate', value: `${avgOpenRate}%`, color: '#f59e0b' },
            ].map((s, i) => (
              <Grid key={i} size={{ xs: 6, sm: 3 }}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: s.color }}>{s.value}</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase' }}>{s.label}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Filter bar */}
          <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
            <TextField
              select
              size="small"
              label="Status"
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="">All Statuses</MenuItem>
              {['Draft', 'Active', 'Paused', 'Completed', 'Archived'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
            <TextField
              select
              size="small"
              label="Type"
              value={typeFilter}
              onChange={e => setTypeFilter(e.target.value)}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="">All Types</MenuItem>
              {['Email', 'LinkedIn', 'MultiChannel'].map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
          </Stack>

          {/* Campaign grid */}
          {filtered.length === 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
              <Typography sx={{ mb: 2 }}>No campaigns yet</Typography>
              <Button variant="contained" onClick={openCreateModal}>Create First Campaign</Button>
            </Box>
          ) : (
            <AzotteGrid
              columns={campaignColumns}
              rows={filtered}
              totalCount={filtered.length}
              page={page}
              setPage={setPage}
              rowsPerPage={rowsPerPage}
              handleRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
              loading={loading}
              onDoubleClick={(row) => navigate(`/engage/campaigns/${row.id}`)}
              onAdd={openCreateModal}
              title="Campaigns"
              actionColumnKey="actions"
            />
          )}
        </Box>
      )}

      {activeTab === 1 && (
        <Box>
          {analyticsData ? (
            <>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {[
                  { label: 'Total Sent', value: analyticsData.totalSent, color: 'primary.main' },
                  { label: 'Overall Open Rate', value: analyticsData.totalSent > 0 ? `${((analyticsData.totalOpened / analyticsData.totalSent) * 100).toFixed(1)}%` : '0%', color: '#22c55e' },
                  { label: 'Overall Reply Rate', value: analyticsData.totalSent > 0 ? `${((analyticsData.totalReplied / analyticsData.totalSent) * 100).toFixed(1)}%` : '0%', color: '#3b82f6' },
                  { label: 'Bounce Rate', value: analyticsData.totalSent > 0 ? `${((analyticsData.totalBounced / analyticsData.totalSent) * 100).toFixed(1)}%` : '0%', color: '#ef4444' },
                ].map((s, i) => (
                  <Grid key={i} size={{ xs: 6, sm: 3 }}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: s.color }}>{s.value}</Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase' }}>{s.label}</Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              <AzotteGrid
                columns={analyticsComparisonColumns}
                rows={analyticsData.campaigns.filter(c => c.analytics)}
                totalCount={analyticsData.campaigns.filter(c => c.analytics).length}
                page={analyticsPage}
                setPage={setAnalyticsPage}
                rowsPerPage={analyticsRowsPerPage}
                handleRowsPerPageChange={(e) => { setAnalyticsRowsPerPage(parseInt(e.target.value)); setAnalyticsPage(0); }}
                loading={false}
                title="Campaign Comparison"
              />
            </>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          )}
        </Box>
      )}

      {/* CREATE CAMPAIGN MODAL */}
      {createModal && (
        <Modal title="Create Campaign" onClose={() => setCreateModal(false)} maxWidth="sm">
          <form onSubmit={handleCreate}>
            <Stack spacing={2}>
              <TextField
                label="Name"
                required
                fullWidth
                size="small"
                value={form.name || ''}
                onChange={e => setForm({ ...form, name: e.target.value })}
              />
              <TextField
                label="Description"
                fullWidth
                size="small"
                multiline
                rows={2}
                value={form.description || ''}
                onChange={e => setForm({ ...form, description: e.target.value })}
              />
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    select
                    label="Type"
                    fullWidth
                    size="small"
                    value={form.type}
                    onChange={e => setForm({ ...form, type: e.target.value })}
                  >
                    {['Email', 'LinkedIn', 'MultiChannel'].map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    select
                    label="Interaction Template"
                    fullWidth
                    size="small"
                    value={form.interactionTemplateId || ''}
                    onChange={e => setForm({ ...form, interactionTemplateId: e.target.value })}
                  >
                    <MenuItem value="">— None —</MenuItem>
                    {templates.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
                  </TextField>
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    select
                    label="Target Segmentation"
                    fullWidth
                    size="small"
                    value={form.targetSegmentationId || ''}
                    onChange={e => setForm({ ...form, targetSegmentationId: e.target.value })}
                    SelectProps={{ displayEmpty: true }}
                  >
                    <MenuItem value="">— All —</MenuItem>
                    {segmentations.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    select
                    label="Target ICP Profile"
                    fullWidth
                    size="small"
                    value={form.targetIcpProfileId || ''}
                    onChange={e => setForm({ ...form, targetIcpProfileId: e.target.value })}
                  >
                    <MenuItem value="">— None —</MenuItem>
                    {icpProfiles.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
                  </TextField>
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    select
                    label="Default Tone"
                    fullWidth
                    size="small"
                    value={form.defaultTone}
                    onChange={e => setForm({ ...form, defaultTone: e.target.value })}
                  >
                    {TONES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    select
                    label="Default Persona"
                    fullWidth
                    size="small"
                    value={form.defaultPersona || ''}
                    onChange={e => setForm({ ...form, defaultPersona: e.target.value })}
                  >
                    <MenuItem value="">— None —</MenuItem>
                    {PERSONAS.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                  </TextField>
                </Grid>
              </Grid>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.useAiPersonalization}
                    onChange={e => setForm({ ...form, useAiPersonalization: e.target.checked })}
                  />
                }
                label="Use AI Personalization"
              />
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Start Date"
                    type="date"
                    fullWidth
                    size="small"
                    value={form.startDate || ''}
                    onChange={e => setForm({ ...form, startDate: e.target.value })}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="End Date"
                    type="date"
                    fullWidth
                    size="small"
                    value={form.endDate || ''}
                    onChange={e => setForm({ ...form, endDate: e.target.value })}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                </Grid>
              </Grid>
            </Stack>
            <DialogActions sx={{ mt: 2, px: 0 }}>
              <Button variant="outlined" onClick={() => setCreateModal(false)}>Cancel</Button>
              <Button variant="contained" type="submit">Create Campaign</Button>
            </DialogActions>
          </form>
        </Modal>
      )}
    </Box>
  );
}
